import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { blogPosts } from '../components/BlogCard';
import { SeasonBadge, SEASONS } from '../components/SeasonBadge';
import { TuneUpBanner } from '../components/TuneUpBanner';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.id === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#14366C] mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#F58220] hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const others = blogPosts.filter(p => p.id !== slug);
  const otherPosts = [...others.filter(p => p.season === post.season), ...others.filter(p => p.season !== post.season)].slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="relative h-[400px] overflow-hidden">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14366C]/90 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <SeasonBadge season={post.season} className="px-3 py-1 text-xs mb-4" />
            <h1 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white max-w-4xl">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-gray-500 mb-8 pb-8 border-b">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {post.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {post.readTime}
              </span>
              <span className="bg-[#F58220]/10 text-[#F58220] px-3 py-1 rounded-full text-sm font-semibold">
                {post.category}
              </span>
            </div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                '--tw-prose-headings': '#14366C',
                '--tw-prose-links': '#F58220',
              }}
            />

            <TuneUpBanner />

            {/* Back Link */}
            <div className="mt-8">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-[#14366C] hover:text-[#F58220] transition-colors font-semibold"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to All Articles
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {otherPosts.length > 0 && (
        <section className="py-12 bg-[#F5F0EB]">
          <div className="container mx-auto px-4">
            <h2 className="font-oswald text-2xl font-bold text-[#14366C] mb-8 text-center">
              More from the {SEASONS[post.season]?.short || 'Blog'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {otherPosts.map(relatedPost => (
                <Link 
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all group"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <SeasonBadge season={relatedPost.season} short className="px-2 py-0.5 text-xs" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-oswald font-bold text-[#14366C] group-hover:text-[#F58220] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2">{relatedPost.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
